import axios from 'axios';
import { fromURL, DEVELOPMENT } from 'common/env/env';

import { type IntlResponse, type SupportedLocale } from './intlTypes';
import LocaleNegotiator from './localeNegotiator';

export const DEFAULT_LOCALE = 'en-GB' as SupportedLocale;
export const DEFAULT_LANGUAGE = 'English (UK)';

export async function ca() {
  return import(/* webpackChunkName: "ca" */ './locale/ca');
}
ca.language = 'català';
ca.alias = ['ca_ES'];

export async function cs() {
  return import(/* webpackChunkName: "cs" */ './locale/cs');
}
cs.language = 'čeština';
cs.alias = ['cs_CZ'];

export async function da() {
  return import(/* webpackChunkName: "da" */ './locale/da');
}
da.language = 'dansk';
da.alias = ['da_DK'];

export async function de() {
  return import(/* webpackChunkName: "de" */ './locale/de');
}
de.language = 'Deutsch (Deutschland)';
de.alias = ['de_DE'];

export async function deAt() {
  return import(/* webpackChunkName: "de-AT" */ './locale/de-AT');
}
deAt.language = 'Deutsch (Österreich)';
deAt.alias = ['de_AT'];

export async function deCh() {
  return import(/* webpackChunkName: "de-CH" */ './locale/de-CH');
}
deCh.language = 'Deutsch (Schweiz)';
deCh.alias = ['de_CH'];

export async function enAu() {
  return import(/* webpackChunkName: "en-AU" */ './locale/en-AU');
}
enAu.language = 'English (Australia)';
enAu.alias = ['en_AU'];

export async function enCa() {
  return import(/* webpackChunkName: "en-CA" */ './locale/en-CA');
}
enCa.language = 'English (Canada)';
enCa.alias = ['en_CA'];

export async function enGb() {
  return import(/* webpackChunkName: "en-GB" */ './locale/en-GB');
}
enGb.language = 'English (UK)';
enGb.alias = ['en', 'en_GB'];

export async function enUs() {
  return import(/* webpackChunkName: "en-US" */ './locale/en-US');
}
enUs.language = 'English (US)';
enUs.alias = ['en_US'];

export async function esEs() {
  return import(/* webpackChunkName: "es-ES" */ './locale/es-ES');
}
esEs.language = 'español';
esEs.alias = ['es_ES'];

export async function fr() {
  return import(/* webpackChunkName: "fr" */ './locale/fr');
}
fr.language = 'français (France)';
fr.alias = ['fr_FR'];

export async function frBe() {
  return import(/* webpackChunkName: "fr-BE" */ './locale/fr-BE');
}
frBe.language = 'français (Belgique)';
frBe.alias = ['fr_BE'];

export async function frCa() {
  return import(/* webpackChunkName: "fr-CA" */ './locale/fr-CA');
}
frCa.language = 'français (Canada)';
frCa.alias = ['fr_CA'];

export async function frCh() {
  return import(/* webpackChunkName: "fr-CH" */ './locale/fr-CH');
}
frCh.language = 'français (Suisse)';
frCh.alias = ['fr_CH'];

export async function it() {
  return import(/* webpackChunkName: "it" */ './locale/it');
}
it.language = 'italiano';
it.alias = ['it_IT'];

export async function nb() {
  return import(/* webpackChunkName: "nb" */ './locale/nb');
}
nb.language = 'norsk';
nb.alias = ['no', 'nb_NO', 'nn_NO'];

export async function nl() {
  return import(/* webpackChunkName: "nl" */ './locale/nl');
}
nl.language = 'Nederlands (Nederland)';
nl.alias = ['nl_NL'];

export async function nlBe() {
  return import(/* webpackChunkName: "nl-BE" */ './locale/nl-BE');
}
nlBe.language = 'Nederlands (België)';
nlBe.alias = ['nl_BE', 'vls-BE', 'vls_Be'];

export async function pl() {
  return import(/* webpackChunkName: "pl" */ './locale/pl');
}
pl.language = 'polski';
pl.alias = ['pl_PL'];

export async function ptPt() {
  return import(/* webpackChunkName: "pt-PT" */ './locale/pt-PT');
}
ptPt.language = 'Português';
ptPt.alias = ['pt_PT'];

export async function svSe() {
  return import(/* webpackChunkName: "sv-SE" */ './locale/sv-SE');
}
svSe.language = 'Svenska';
svSe.alias = ['sv_SE'];

export const locales = {
  ca, // Catalan
  cs, // Czech
  da, // Danish
  de, // German (Deutschland)
  'de-CH': deCh, // German (Switzerland)
  'de-AT': deAt, // German (Austria)
  'en-AU': enAu, // English (Australia)
  'en-CA': enCa, // English (Canada)
  'en-GB': enGb, // English (GB)
  'en-US': enUs, // English (US)
  'es-ES': esEs, // Spanish
  fr, // French (France)
  'fr-BE': frBe, // French (Belgium)
  'fr-CA': frCa, // French (Canada)
  'fr-CH': frCh, // French (Switzerland)
  it, // Italian
  nb, // Norwegian
  nl, // Dutch
  'nl-BE': nlBe, // Dutch (Belgium)
  pl, // Polish
  'pt-PT': ptPt, // Portuguese
  'sv-SE': svSe, // Swedish
};

Object.freeze(locales);

async function fetchLocale(locale: SupportedLocale) {
  const resolvedLocale = LocaleNegotiator.lookup(
    locale,
    locales,
    DEFAULT_LOCALE,
  ) as SupportedLocale;

  const out = locales[resolvedLocale]() as unknown as IntlResponse;
  if (fromURL(window.location) === DEVELOPMENT) {
    const messages = axios
      .get<IntlResponse['messages']>(`/intl/${resolvedLocale}/messages.json`)
      .then((res) => res.data);
    const values = await Promise.all([out, messages]);
    return {
      ...values[0],
      messages: values[1],
    };
  }

  return out;
}

function getBrowserLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  let locale = window.navigator.language || window.navigator.languages?.[0];

  // if android, we take navigator.language as navigator.languages seems unsupported
  if (navigator.userAgent.toLowerCase().includes('android')) locale = window.navigator.language;

  return locale ?? DEFAULT_LOCALE;
}

function getSupportedLocales() {
  return Object.keys(locales);
}

function getSupportedLanguages() {
  const languages = {} as Record<SupportedLocale, string>;
  (Object.keys(locales) as SupportedLocale[]).forEach((locale) => {
    languages[locale] = locales[locale].language;
  });
  return languages;
}

export default {
  fetchLocale,
  getBrowserLocale,
  getSupportedLocales,
  getSupportedLanguages,
};
